import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import { ProductBatchDto, ProductType, StockDto } from '@services/dtos'
import { useEffect, useMemo } from 'react'
import { Container, Tooltip } from '@mui/material'
import { useSelector } from '@state/store'
import { useAppDispatch } from '@hooks/reduxHooks'
import { getStocks, resetActiveStocksTab, StockStatesTabs, updateStocksFilters } from '@state/reducers/stockReducer'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { ProductTypeLabels } from '@utils/constants'
import { getProductBatches, setProductBatchFilters } from '@state/reducers/productBatchReducer'
import StockStatesTableToolbar from './Toolbars/StockStatesTableToolbar'
import moment from 'moment'
import { mbqToGbq } from '../Helpers'

export default function StockStatesTable() {
  const dispatch = useAppDispatch()
  const { 
    stocks, stockFilters, stockTotalCount, stockLoading, 
    productBatches, productBatchesFilters, productBatchesTotalCount, productBatchesLoading,
    activeTab
  } = useSelector(({ stock, productBatch }) => ({
    stocks: stock.stocks.datas,
    stockTotalCount: stock.stocks.totalCount,
    stockLoading: stock.loading,
    stockFilters: stock.filters,
    productBatches: productBatch.items.datas,
    productBatchesTotalCount: productBatch.items.totalCount,
    productBatchesLoading: productBatch.loading,
    productBatchesFilters: productBatch.filters,
    activeTab: stock.activeStocksTab,
  }))

  useEffect(() => {
    dispatch(getStocks())
    return () => {
      dispatch(updateStocksFilters({ productGenericName: undefined, productType: undefined, alertStock: false }))
      dispatch(setProductBatchFilters({ productGenericName: undefined, storageLocationId: undefined, 
        productType: undefined, search: undefined }))
      dispatch(resetActiveStocksTab())
    }
  }, [])

  useEffect(() => {
    if (activeTab === StockStatesTabs.DCI) {
      dispatch(getStocks())
    }
  }, [stockFilters, activeTab])

  useEffect(() => {
    if (activeTab === StockStatesTabs.ProductBatches) {
      dispatch(getProductBatches())
    }
  }, [productBatchesFilters, activeTab])

  useEffect(() => {
    dispatch(activeTab === StockStatesTabs.DCI ? updateStocksFilters({ page: 0 }) : setProductBatchFilters({ page: 0 }))
  }, [activeTab])

  const isAlertExpiryDate = (expiryDate: Date) => {
    if (!expiryDate) {
      return false
    }
    return moment(expiryDate).isBefore(moment().add(1, 'months'))
  }

  const stockColumns: GridColumns<StockDto> = useMemo(
    () => [
      { field: 'genericName', headerName: 'DCI', width: 200 },
      { field: 'units', headerName: 'Quantité', width: 100, type: 'number', renderCell: (params) => {
          if (params.row.isAlertStock) {
            return (
              <div className="flex items-center">
                <Tooltip title="Alerte stock" arrow>
                  <WarningAmberIcon className="text-red-500 mr-1" fontSize='small' />
                </Tooltip>
                {params.value}
              </div>
            )
          }
          return params.value
        }
      },
      { field: 'examUnits', headerName: 'Nb examens possible', width: 180, type: 'number' },
    ],
    [],
  )
  const productBatchColumns: GridColumns<ProductBatchDto> = useMemo(
    () => [
      { field: 'lotNumber', headerName: 'N° lot', width: 160 },
      { field: 'totalUnits', headerName: 'Quantité', width: 80, type: 'number' },
      { field: 'productGenericName', headerName: 'DCI', width: 200, 
        valueGetter: (params) => params.row.productCatalog.genericName },
      { field: 'productName', headerName: 'Nom produit', width: 250, 
        valueGetter: (params) => params.row.productCatalog.name },
      { field: 'storageLocations', headerName: 'Stockage / quantité', width: 200, 
        valueGetter: (params) => params.row.storageLocations.map(sl => 
          `${sl.storageLocation.name} (${sl.units})`).join(', ') },
      { field: 'productType', headerName: 'Type produit', width: 120, 
        valueGetter: (params) => ProductTypeLabels[params.row.productCatalog.type] },
      { field: 'expiryAt', headerName: 'Date expiration', width: 120,
        renderCell: (params) => {
          if (!params.row.expiryAt || params.row.totalUnits < 1) { 
            return null
          }
          const formattedDate = moment(params.row.expiryAt).format('DD/MM/YYYY')
          if (isAlertExpiryDate(params.row.expiryAt)) {
            return (
              <div className="flex items-center">
                <Tooltip title="Alerte expiration" arrow>
                  <WarningAmberIcon className="text-red-500 mr-1" fontSize='small' />
                </Tooltip>
                {formattedDate}
              </div>
            )
          }
          return formattedDate
        }
      },
      { field: 'receivedAt', headerName: 'Date réception', width: 115, 
        valueGetter: (params) => params.row.receivedAt ? moment(params.row.receivedAt).format('DD/MM/YYYY HH:mm') : null },
      { field: 'productionSite', headerName: 'Site production', width: 85 },
      { 
        field: 'activity', 
        headerName: 'Activité', 
        width: 170,
        valueGetter: (params) => {
          if (!params.row.activityAtRefMbq) {
            return null
          }
          const isGenerator = params.row.productCatalog?.type === ProductType.GENERATOR
          const activity = isGenerator
            ? `${mbqToGbq(params.row.activityAtRefMbq)} GBq`
            : `${params.row.activityAtRefMbq} MBq`
          if (!params.row.activityRefAt) {
            return activity
          }
          return `${activity} (${moment.utc(params.row.activityRefAt).format('DD/MM/YYYY HH:mm')} UTC)`
        }
      },
      { field: 'notes', headerName: 'Notes', width: 250 },
    ],
    [],
  )
  
  return (
    <Container maxWidth={false} style={{ height: '100%', padding: '0px 30px' }}>
      {activeTab === StockStatesTabs.DCI ? <DataGridPro
        density="standard"
        pagination
        pageSize={20}
        rowsPerPageOptions={[20]}
        paginationMode="server"
        style={{
          height: 'calc(100vh - 198px)',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '15px 15px 25px 25px',
        }}
        components={{ Toolbar: StockStatesTableToolbar }}
        columns={stockColumns}
        rows={stocks}
        rowCount={stockTotalCount}
        loading={stockLoading}
        disableSelectionOnClick
        disableMultipleSelection
        onPageChange={(page) => {
          dispatch(updateStocksFilters({ page }))
        }}
        getRowId={(row) => row.genericName}
        sx={{
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f8f8f8',
          }
        }}
        getRowClassName={(params) => params.row.isAlertStock ? '!bg-red-100' : '' }
      /> : (
        <DataGridPro
          density="standard"
          pagination
          pageSize={20}
          rowsPerPageOptions={[20]}
          paginationMode="server"
          style={{
            height: 'calc(100vh - 198px)',
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '15px 15px 25px 25px',
          }}
          components={{ Toolbar: StockStatesTableToolbar }}
          columns={productBatchColumns}
          rows={productBatches}
          rowCount={productBatchesTotalCount}
          loading={productBatchesLoading}
          disableSelectionOnClick
          disableMultipleSelection
          onPageChange={(page) => {
            dispatch(setProductBatchFilters({ page }))
          }}
          sx={{
            '& .MuiDataGrid-row:nth-of-type(even)': {
              backgroundColor: '#f8f8f8',
            }
          }}
          getRowClassName={(params) => isAlertExpiryDate(params.row.expiryAt) ? '!bg-red-100' : '' }
        />
      )}
    </Container>
  )
}
